import {testIdProps} from '@github-ui/test-id-props'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button, Truncate} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {
  SliceItemDeselected,
  SliceItemSelected,
  SlicerDeselect,
  SlicerHideEmpty,
  SlicerPanelUI,
  SlicerShowEmpty,
} from '../../api/stats/contracts'
import {type SliceValue, useSliceBy} from '../../features/slicing/hooks/use-slice-by'
import {ViewerPrivileges} from '../../helpers/viewer-privileges'
import {usePostStats} from '../../hooks/common/use-post-stats'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {useViewType} from '../../hooks/use-view-type'
import {useViews} from '../../hooks/use-views'
import {BulkAddItemsProvider} from '../side-panel/bulk-add/bulk-add-items-provider'
import {SliceByMenu} from '../slice-by-menu'
import {SLICER_PANEL_DEFAULT_WIDTH, SLICER_PANEL_NARROW, SLICER_PANEL_WIDE} from './constants'
import {matchesSlicerSearch, type SliceByFilterMode} from './slicer-filter-controls'
import {SlicerFilterControls, useSlicerSearch} from './slicer-filter-controls'
import {SlicerGroupByItems, SlicerIterationItems} from './slicer-items'
import itemStyles from './slicer-items.module.css'
import type {SlicerItemGroup} from './slicer-items-provider'
import styles from './slicer-panel.module.css'
import {SlicerPanelResizer} from './slicer-panel-resizer'

export function SlicerPanel({
  slicerItems,
  totalSlicerItemCount,
}: {
  slicerItems: Array<SlicerItemGroup> | null
  totalSlicerItemCount: number
}) {
  const {sliceField, sliceValue, setSliceValue, sliceByPanelWidth, setSliceByPanelWidth} = useSliceBy()
  const [isSliceByMenuOpen, setIsSliceByMenuOpen] = useState(false)
  const [showEmptyItems, setShowEmptyItems] = useState(false)
  const {currentView} = useViews()
  const {viewType} = useViewType()
  const {postStats} = usePostStats()
  const {hasWritePermissions} = ViewerPrivileges()
  const {memex_slice_by_filtering} = useEnabledFeatures()

  const [localPanelWidth, setLocalPanelWidth] = useState<Record<number, number>>({})
  const {searchQuery, setSearchQuery, deferredSearchQuery} = useSlicerSearch()
  const [filterMode, setFilterMode] = useState<SliceByFilterMode>('include')

  // Reset client filter when the slice-by field changes
  const sliceFieldId = sliceField?.id
  useEffect(() => {
    setSearchQuery('')
    setFilterMode('include')
  }, [sliceFieldId, setSearchQuery])

  const anchorRef = useRef<HTMLButtonElement>(null)

  // Apply text search filtering on top of include/exclude filtering
  const searchFilteredItems = useMemo(() => {
    if (!memex_slice_by_filtering) return slicerItems
    if (!slicerItems || !deferredSearchQuery) return slicerItems
    return slicerItems.filter(item => matchesSlicerSearch(item.value, deferredSearchQuery, filterMode))
  }, [memex_slice_by_filtering, slicerItems, deferredSearchQuery, filterMode])

  const toggleSliceValue = useCallback(
    (value: SliceValue) => {
      if (!sliceField || !currentView) return

      const toggleOn = value !== sliceValue

      setSliceValue(currentView.number, toggleOn ? value : null)

      postStats({
        name: toggleOn ? SliceItemSelected : SliceItemDeselected,
        memexProjectColumnId: sliceField.id,
        ui: SlicerPanelUI,
        context: JSON.stringify({layout: viewType}),
      })
    },
    [sliceField, currentView, sliceValue, setSliceValue, postStats, viewType],
  )

  const deselectSelection = useCallback(() => {
    if (!sliceField || !currentView) return

    setSliceValue(currentView.number, null)

    postStats({
      name: SlicerDeselect,
      memexProjectColumnId: sliceField.id,
      ui: SlicerPanelUI,
      context: JSON.stringify({layout: viewType}),
    })
  }, [sliceField, currentView, setSliceValue, postStats, viewType])

  const showEmptySlicerItems = useCallback(
    (show: boolean) => {
      if (!sliceField || !currentView) return

      setShowEmptyItems(show)

      postStats({
        name: show ? SlicerShowEmpty : SlicerHideEmpty,
        memexProjectColumnId: sliceField.id,
        ui: SlicerPanelUI,
        context: JSON.stringify({layout: viewType}),
      })
    },
    [sliceField, currentView, postStats, viewType],
  )

  const renderSlicerItems = useCallback(() => {
    if (!sliceField || !searchFilteredItems) return null

    switch (sliceField.dataType) {
      case MemexColumnDataType.Iteration:
        return (
          <SlicerIterationItems
            sliceValue={sliceValue}
            onSliceValueChange={toggleSliceValue}
            slicerItems={searchFilteredItems}
            showEmptySlicerItems={showEmptyItems}
            setShowEmptySlicerItems={showEmptySlicerItems}
          />
        )
      default:
        return (
          <SlicerGroupByItems
            sliceValue={sliceValue}
            onSliceValueChange={toggleSliceValue}
            slicerItems={searchFilteredItems}
            showEmptySlicerItems={showEmptyItems}
            setShowEmptySlicerItems={showEmptySlicerItems}
          />
        )
    }
  }, [sliceField, searchFilteredItems, sliceValue, toggleSliceValue, showEmptyItems, showEmptySlicerItems])

  // Handle resizing the slicer panel while the user is dragging the resizer control
  const handleResize = useCallback(
    (width: number) => {
      if (!currentView) return

      setLocalPanelWidth(prev => {
        return {
          ...prev,
          [currentView.number]: width,
        }
      })
    },
    [currentView, setLocalPanelWidth],
  )

  // Handle resizing the slicer panel when the user has stopped
  // dragging the resizer control or has double clicked it
  // This will update the slice by panel width server side
  const handleResizeEnd = useCallback(
    (width: number) => {
      if (!currentView) return

      setLocalPanelWidth(prev => {
        const newState = {...prev}
        delete newState[currentView.number]

        return newState
      })

      setSliceByPanelWidth(width)
    },
    [currentView, setSliceByPanelWidth],
  )

  if (!currentView) return null

  const panelWidth = localPanelWidth[currentView.number] ?? sliceByPanelWidth ?? SLICER_PANEL_DEFAULT_WIDTH

  const panelClassNames = clsx(itemStyles.SlicerPanel, {
    [itemStyles.Wide]: panelWidth > SLICER_PANEL_WIDE,
    [itemStyles.Narrow]: panelWidth < SLICER_PANEL_NARROW,
    [itemStyles.WritePermissions]: !hasWritePermissions,
  })

  return (
    /**
     * The height: 0 and minHeight: '100%' trickery is needed to allow scrolling the slicer panel
     * within the grid container. Without this, the slicer panel will not scroll correctly.
     *
     * See: https://stackoverflow.com/a/43312314
     * See: https://stackoverflow.com/a/43352777
     */
    <div className={styles.Box}>
      <div
        {...testIdProps('slicer-panel')}
        className={panelClassNames}
        style={{
          width: `${panelWidth}px`,
        }}
      >
        <BulkAddItemsProvider>
          {sliceField && slicerItems ? (
            <>
              <div className={styles.Box_1}>
                <Button
                  variant="invisible"
                  ref={anchorRef}
                  aria-expanded={isSliceByMenuOpen}
                  onClick={() => setIsSliceByMenuOpen(!isSliceByMenuOpen)}
                  trailingVisual={TriangleDownIcon}
                  className={styles.Button}
                >
                  <Truncate
                    {...testIdProps('slicer-panel-title')}
                    id="slicer-panel-title"
                    title={sliceField.name}
                    className={styles.Truncate}
                  >
                    {sliceField.name}
                  </Truncate>
                </Button>
                {sliceValue && (
                  <Button variant="invisible" onClick={deselectSelection} size="small" className={styles.Button_1}>
                    Deselect
                  </Button>
                )}
              </div>
              <SliceByMenu
                anchorRef={anchorRef}
                open={isSliceByMenuOpen}
                setOpen={setIsSliceByMenuOpen}
                ui={SlicerPanelUI}
              />

              {memex_slice_by_filtering && (
                <SlicerFilterControls
                  totalItemCount={totalSlicerItemCount}
                  visibleItemCount={searchFilteredItems?.length ?? 0}
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                  filterMode={filterMode}
                  onFilterModeChange={setFilterMode}
                />
              )}

              {renderSlicerItems()}
            </>
          ) : null}
        </BulkAddItemsProvider>
      </div>
      {hasWritePermissions && (
        <SlicerPanelResizer onResize={handleResize} onResizeEnd={handleResizeEnd} width={panelWidth} />
      )}
    </div>
  )
}
